export const routingLooksMasked = (value: string): boolean =>
  value.includes("*") || /^\d{4}$/.test(value);

/** Last-4 storage or X-prefix display mask — not a freshly typed / excel value. */
export const accountNumberLooksMasked = (value: string): boolean =>
  value.includes("X") || /^\d{1,4}$/.test(value);

export const isValidRoutingNumber = (value: string): boolean =>
  /^\d{9}$/.test(value);

const toRoutingString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

/**
 * True when bankDetails look like a masked GET echo (not freshly typed plaintext).
 * Mirrors draw-service looksMaskedBankDetails: a lone 4-digit routing is NOT enough;
 * last-4 needs corroboration (X-account, asterisks, or encryption metadata).
 */
export function bankDetailsLookLikeMaskedEcho(bd: any): boolean {
  if (!bd || typeof bd !== "object") return false;

  const routingStr = bd.routing != null ? String(bd.routing) : "";
  const accountNumberStr =
    bd.accountNumber != null ? String(bd.accountNumber) : "";
  const accountNameStr = bd.accountName != null ? String(bd.accountName) : "";

  const accountNumberMasked = accountNumberStr.startsWith("X");
  const routingHasAsterisks = routingStr.includes("*");
  const accountNameHasAsterisks = accountNameStr.includes("*");
  const hasEncryptionIds = !!(
    bd.routingEncryptionId ||
    bd.accountNumberEncryptionId ||
    bd.accountNameEncryptionId ||
    bd.isEncrypted === true
  );
  const routingIsLast4 = /^\d{4}$/.test(routingStr);
  const routingMasked =
    routingHasAsterisks ||
    (routingIsLast4 &&
      (accountNumberMasked || accountNameHasAsterisks || hasEncryptionIds));

  const accountNameMasked =
    accountNameHasAsterisks ||
    (accountNameStr.length > 0 &&
      accountNameStr.length <= 4 &&
      (routingMasked || accountNumberMasked));

  return routingMasked || accountNumberMasked || accountNameMasked;
}

/**
 * Routing value Save/Submit should 9-digit-check. Matches the edit input:
 * decrypted/typed first, else the stored value still shown on screen.
 * A masked last-4 / stars is published as "" so a previous-draw mask is not
 * treated as an invalid short routing. Excel-uploaded plaintext like "67565"
 * is not a mask, so it is published and the 9-digit rule can fire.
 */
export const visibleRoutingForValidation = (
  showingEdit: boolean,
  editDecryptedRouting: string | undefined,
  storedRouting: unknown
): string => {
  if (!showingEdit) return "";
  if (editDecryptedRouting != null) return String(editDecryptedRouting);
  const stored = toRoutingString(storedRouting);
  return routingLooksMasked(stored) ? "" : stored;
};
